import {
  useCallback,
  useEffect,
  useState
} from "react";

import * as offerApi
  from "../../../api/offers/offers.api.js";

import {
  DEFAULT_OFFER_FILTERS
} from "../constants/offer.constants.js";

import {
  normalizeOffer
} from "../utils/offer.utils.js";


/*
|--------------------------------------------------------------------------
| ERROR MESSAGE HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (error) => {

  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};


/*
|--------------------------------------------------------------------------
| HOOK
|--------------------------------------------------------------------------
*/

export const useOffers = (
  initialFilters = {}
) => {

  /*
  |--------------------------------------------------------------------------
  | LIST STATE
  |--------------------------------------------------------------------------
  */

  const [
    offers,
    setOffers
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    error,
    setError
  ] = useState(null);

  const [
    filters,
    setFilters
  ] = useState({
    ...DEFAULT_OFFER_FILTERS,
    ...initialFilters
  });


  /*
  |--------------------------------------------------------------------------
  | SINGLE OFFER STATE
  |--------------------------------------------------------------------------
  */

  const [
    selectedOffer,
    setSelectedOffer
  ] = useState(null);

  const [
    selectedOfferLoading,
    setSelectedOfferLoading
  ] = useState(false);

  const [
    selectedOfferError,
    setSelectedOfferError
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | MUTATION STATE
  |--------------------------------------------------------------------------
  */

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    mutationError,
    setMutationError
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | FETCH OFFERS
  |--------------------------------------------------------------------------
  */

  const fetchOffers = useCallback(
    async (customFilters = {}) => {

      setLoading(true);
      setError(null);

      try {

        const params = {
          ...(customFilters.status
            ? {
                status:
                  customFilters.status
              }
            : {}),

          ...(customFilters.clientId
            ? {
                clientId:
                  customFilters.clientId
              }
            : {}),

          ...(customFilters.queryId
            ? {
                queryId:
                  customFilters.queryId
              }
            : {})
        };


        const response =
          await offerApi.getOffers(
            params
          );


        const data =
          response?.data?.data ??
          response?.data ??
          [];


        const normalized =
          Array.isArray(data)
            ? data.map(
                normalizeOffer
              )
            : [];


        setOffers(
          normalized
        );


        return normalized;

      } catch (err) {

        console.error(
          "Failed to fetch offers:",
          err
        );

        setError(
          getErrorMessage(err)
        );

        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | FETCH SINGLE OFFER
  |--------------------------------------------------------------------------
  */

  const fetchOffer = useCallback(
    async (id) => {

      if (!id) {
        return null;
      }


      setSelectedOfferLoading(
        true
      );

      setSelectedOfferError(
        null
      );


      try {

        console.log(
          "[OFFER] Fetching offer:",
          id
        );


        const response =
          await offerApi.getOffer(
            id
          );


        const data =
          response?.data?.data ??
          response?.data ??
          null;


        const normalized =
          data
            ? normalizeOffer(data)
            : null;


        setSelectedOffer(
          normalized
        );


        return normalized;

      } catch (err) {

        console.error(
          "[OFFER] Failed to fetch offer:",
          err
        );


        setSelectedOfferError(
          getErrorMessage(err)
        );


        throw err;

      } finally {

        setSelectedOfferLoading(
          false
        );

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CREATE
  |--------------------------------------------------------------------------
  */

  const createOffer = useCallback(
    async (payload) => {

      setSaving(true);
      setMutationError(null);

      try {

        const response =
          await offerApi.createOffer(
            payload
          );


        const data =
          response?.data?.data ??
          response?.data ??
          null;


        const normalized =
          data
            ? normalizeOffer(data)
            : null;


        if (normalized) {

          setSelectedOffer(
            normalized
          );

        }


        await fetchOffers(
          filters
        );


        return normalized;

      } catch (err) {

        setMutationError(
          getErrorMessage(err)
        );

        throw err;

      } finally {

        setSaving(false);

      }

    },
    [
      fetchOffers,
      filters
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | UPDATE
  |--------------------------------------------------------------------------
  */

  const updateOffer = useCallback(
    async (
      id,
      payload
    ) => {

      setSaving(true);
      setMutationError(null);

      try {

        const response =
          await offerApi.updateOffer(
            id,
            payload
          );


        const data =
          response?.data?.data ??
          response?.data ??
          null;


        const normalized =
          data
            ? normalizeOffer(data)
            : null;


        if (normalized) {

          setSelectedOffer(
            normalized
          );

        }


        await fetchOffers(
          filters
        );


        return normalized;

      } catch (err) {

        setMutationError(
          getErrorMessage(err)
        );

        throw err;

      } finally {

        setSaving(false);

      }

    },
    [
      fetchOffers,
      filters
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | CHANGE STATUS
  |--------------------------------------------------------------------------
  */

  const changeOfferStatus =
    useCallback(
      async (
        id,
        status
      ) => {

        setSaving(true);
        setMutationError(null);

        try {

          const response =
            await offerApi.changeOfferStatus(
              id,
              status
            );


          const data =
            response?.data?.data ??
            response?.data ??
            null;


          const normalized =
            data
              ? normalizeOffer(data)
              : null;


          if (normalized) {

            setSelectedOffer(
              normalized
            );

          }


          await fetchOffers(
            filters
          );


          return normalized;

        } catch (err) {

          setMutationError(
            getErrorMessage(err)
          );

          throw err;

        } finally {

          setSaving(false);

        }

      },
      [
        fetchOffers,
        filters
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | DELETE OFFER
  |--------------------------------------------------------------------------
  */

  const deleteOffer =
    useCallback(
      async (id) => {

        setSaving(true);
        setMutationError(null);

        try {

          const response =
            await offerApi.deleteOffer(
              id
            );


          setOffers(
            (current) =>
              current.filter(
                (offer) =>
                  String(offer.id) !==
                  String(id)
              )
          );


          if (
            selectedOffer &&
            String(selectedOffer.id) ===
            String(id)
          ) {

            setSelectedOffer(
              null
            );

          }


          return response;

        } catch (err) {

          setMutationError(
            getErrorMessage(err)
          );

          throw err;

        } finally {

          setSaving(false);

        }

      },
      [
        selectedOffer
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | ADD ITEM
  |--------------------------------------------------------------------------
  */

  const addOfferItem =
    useCallback(
      async (
        offerId,
        payload
      ) => {

        setSaving(true);
        setMutationError(null);

        try {

          const response =
            await offerApi.addOfferItem(
              offerId,
              payload
            );


          const data =
            response?.data?.data ??
            response?.data ??
            null;


          if (
            selectedOffer &&
            String(selectedOffer.id) ===
            String(offerId)
          ) {

            await fetchOffer(
              offerId
            );

          }


          return data;

        } catch (err) {

          setMutationError(
            getErrorMessage(err)
          );

          throw err;

        } finally {

          setSaving(false);

        }

      },
      [
        selectedOffer,
        fetchOffer
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | UPDATE ITEM
  |--------------------------------------------------------------------------
  */

  const updateOfferItem =
    useCallback(
      async (
        offerId,
        itemId,
        payload
      ) => {

        setSaving(true);
        setMutationError(null);

        try {

          const response =
            await offerApi.updateOfferItem(
              offerId,
              itemId,
              payload
            );


          const data =
            response?.data?.data ??
            response?.data ??
            null;


          if (
            selectedOffer &&
            String(selectedOffer.id) ===
            String(offerId)
          ) {

            await fetchOffer(
              offerId
            );

          }


          return data;

        } catch (err) {

          setMutationError(
            getErrorMessage(err)
          );

          throw err;

        } finally {

          setSaving(false);

        }

      },
      [
        selectedOffer,
        fetchOffer
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | DELETE ITEM
  |--------------------------------------------------------------------------
  */

  const deleteOfferItem =
    useCallback(
      async (
        offerId,
        itemId
      ) => {

        setSaving(true);
        setMutationError(null);

        try {

          const response =
            await offerApi.deleteOfferItem(
              offerId,
              itemId
            );


          if (
            selectedOffer &&
            String(selectedOffer.id) ===
            String(offerId)
          ) {

            await fetchOffer(
              offerId
            );

          }


          return response;

        } catch (err) {

          setMutationError(
            getErrorMessage(err)
          );

          throw err;

        } finally {

          setSaving(false);

        }

      },
      [
        selectedOffer,
        fetchOffer
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | FILTERS
  |--------------------------------------------------------------------------
  */

  const updateFilters =
    useCallback(
      (updates) => {

        setFilters(
          (current) => ({
            ...current,
            ...updates
          })
        );

      },
      []
    );


  const resetFilters =
    useCallback(
      () => {

        setFilters({
          ...DEFAULT_OFFER_FILTERS
        });

      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | CLEAR ERRORS
  |--------------------------------------------------------------------------
  */

  const clearErrors =
    useCallback(
      () => {

        setError(null);

        setSelectedOfferError(null);

        setMutationError(null);

      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    fetchOffers(
      filters
    );

  }, []);


  /*
  |--------------------------------------------------------------------------
  | RETURN
  |--------------------------------------------------------------------------
  */

  return {

    offers,
    selectedOffer,

    filters,
    updateFilters,
    resetFilters,
    fetchOffers,

    fetchOffer,
    setSelectedOffer,

    createOffer,
    updateOffer,
    deleteOffer,

    changeOfferStatus,

    addOfferItem,
    updateOfferItem,
    deleteOfferItem,

    loading,
    selectedOfferLoading,
    saving,

    error,
    selectedOfferError,
    mutationError,

    clearErrors
  };
};